import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { AdminUser } from "./types";

/** Owns the admin users list: filters, pagination, fetch, drawer selection,
 *  and shared mutation helper. Extracted from UsersTab.tsx. */
export const useAdminUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tier, setTier] = useState<string>("any");
  const [role, setRole] = useState<string>("any");
  const [bannedOnly, setBannedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | null>(null);

  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tier, role, bannedOnly]);

  const reload = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-list-users", {
      body: {
        q: debouncedSearch || undefined,
        tier: tier === "any" ? undefined : tier,
        role: role === "any" ? undefined : role,
        banned: bannedOnly || undefined,
        limit: 25,
        cursor: page,
      },
    });
    setLoading(false);
    if (error) {
      toast.error(`Could not load users: ${error.message}`);
      return;
    }
    const payload = data as { users: AdminUser[]; next_cursor: number | null } | null;
    setRows(payload?.users ?? []);
    setNextCursor(payload?.next_cursor ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      await reload();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, tier, role, bannedOnly, page]);

  // Auto-open drawer when arriving with ?user=<id>
  useEffect(() => {
    const target = searchParams.get("user");
    if (!target) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.functions.invoke("admin-list-users", {
        body: { q: target, limit: 5, cursor: 1 },
      });
      if (cancelled) return;
      const payload = data as { users: AdminUser[] } | null;
      const found = payload?.users.find((u) => u.id === target);
      if (found) setSelected(found);
      setSearchParams((sp) => { sp.delete("user"); return sp; }, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("user")]);

  const refreshSelected = async () => {
    if (!selected) return;
    const { data } = await supabase.functions.invoke("admin-list-users", {
      body: { q: selected.email ?? undefined, limit: 5, cursor: 1 },
    });
    const payload = data as { users: AdminUser[] } | null;
    const fresh = payload?.users.find((u) => u.id === selected.id) ?? null;
    if (fresh) {
      setSelected(fresh);
      setRows((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
    }
  };

  const callAction = async (
    action: "set_user_tier" | "send_password_reset" | "ban_user" | "unban_user",
    extra: Record<string, unknown> = {},
  ) => {
    if (!selected) return;
    setActionBusy(true);
    const { error } = await supabase.functions.invoke("admin-user-actions", {
      body: { action, target_user_id: selected.id, ...extra },
    });
    setActionBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Done");
    await refreshSelected();
  };

  return {
    search, setSearch,
    debouncedSearch, tier, setTier, role, setRole,
    bannedOnly, setBannedOnly,
    page, setPage,
    rows, setRows,
    loading, nextCursor,
    selected, setSelected,
    actionBusy,
    refreshSelected, callAction, reload,
  };
};
